/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * @override
 * Checkout header: desktop 3-part (Continue shopping | Logo | Need help), mobile 2 rows.
 * "Continue shopping" redirects to the last page the customer was on before checkout.
 * No inline styles; uses CheckoutPage theme only.
 */
import React, {useState} from 'react';
import {useIntl} from 'react-intl';

import {BrandLogo, CheckoutContinueBackIcon} from '@amblique/king-living/app/components/icons';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Box, Button, Flex, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {HOME_HREF} from '@salesforce/retail-react-app/app/constants';

import {getContinueShoppingPath} from '@amblique/king-living/app/pages/checkout/util/checkout-context';

const CONTACT_US_PATH = '/contact-us';
const DEFAULT_CONTINUE_SHOPPING_PATH = '/cart';

const CheckoutHeader = () => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('CheckoutPage', {});
  const [continueShoppingTo] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_CONTINUE_SHOPPING_PATH;

    return getContinueShoppingPath(DEFAULT_CONTINUE_SHOPPING_PATH);
  });

  const continueShoppingTitle = intl.formatMessage({
    defaultMessage: 'Continue shopping',
    id: 'checkout_header.link.continue_shopping_2',
  });

  const renderContinueShoppingLink = () => (
    <Button
      as={Link}
      leftIcon={<CheckoutContinueBackIcon boxSize="20px" />}
      title={continueShoppingTitle}
      to={continueShoppingTo}
      variant="unstyled"
      {...styles.linkContinue}
    >
      {continueShoppingTitle}
    </Button>
  );

  const renderLogoLink = () => (
    <Link title="Back to homepage" to={HOME_HREF} {...styles.linkLogo}>
      <BrandLogo {...styles.logo} />
    </Link>
  );

  const needHelpTitle = intl.formatMessage({
    defaultMessage: 'Need help?',
    id: 'checkout_header.link.need_help',
  });

  const renderNeedHelpLink = () => (
    <Button as={Link}
      target="_blank"
      title={needHelpTitle}
      to={CONTACT_US_PATH}
      variant="unstyled"
      {...styles.linkNeedHelp}
    >
      {needHelpTitle}
    </Button>
  );

  return (
    <Box as="header" {...styles.header}>
      <Box {...styles.headerInner}>
        {/* Desktop: single row — Left: Continue shopping | Middle: Logo | Right: Need help */}
        <Flex {...styles.headerDesktopRow}>
          <Box>{renderContinueShoppingLink()}</Box>
          <Box {...styles.headerLogoCenter}>{renderLogoLink()}</Box>
          <Box>{renderNeedHelpLink()}</Box>
        </Flex>

        {/* Mobile: row 1 — Logo left, Need help right */}
        <Flex {...styles.headerMobileRow1}>
          <Box>{renderLogoLink()}</Box>
          <Box>{renderNeedHelpLink()}</Box>
        </Flex>

        {/* Mobile: row 2 — Continue shopping */}
        <Flex {...styles.headerMobileRow2}>
          {renderContinueShoppingLink()}
        </Flex>
      </Box>
    </Box>
  );
};

export default CheckoutHeader;
